// src/components/CommentList.tsx
import { useEffect, useState } from "react";
import { supabase } from "../supabase/supabaseClient";
import { useUser } from "../contexts/UserContext";
import { usePosts } from "../contexts/PostContext";
import { ThumbsUp } from "lucide-react";

interface Comment {
  id: number;
  post_id: number;
  parent_id?: number | null;
  author_id: string;
  nickname: string | null;
  writer_type?: string | null;
  content: string;
  created_at: string;
}

const typeLabel: Record<string, string> = {
  artist: "예술가",
  worker: "종사자",
  fan: "애호가",
};

export default function CommentList({
  postId,
  boardType,
}: {
  postId: number;
  boardType?: string;
}) {
  const { user } = useUser();
  const [comments, setComments] = useState<Comment[]>([]);
  const [content, setContent] = useState("");
  const [replyTo, setReplyTo] = useState<number | null>(null);
  const [replyContent, setReplyContent] = useState<{
    [parentId: number]: string;
  }>({});
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingContent, setEditingContent] = useState("");
  const [loading, setLoading] = useState(false);

  const { likes, myReactions, handleReaction, loadLikes } = usePosts();

  const isAbtalk = ["artist", "worker", "fan"].includes(boardType || "");
  const isAnonymousBoard = boardType === "익명게시판";

  function countCommentLikes(commentId: number, vote: number) {
    return likes.filter(
      (l) =>
        l.target_type === "comment" &&
        l.target_id === commentId &&
        l.vote === vote
    ).length;
  }

  async function fetchComments() {
    const { data } = await supabase
      .from("comments")
      .select("*")
      .eq("post_id", postId)
      .order("created_at", { ascending: true });
    setComments(data || []);
    if (data && data.length > 0) {
      const ids = data.map((c: Comment) => c.id);
      await loadLikes("comment", ids);
    }
  }

  useEffect(() => {
    fetchComments();
    // eslint-disable-next-line
  }, [postId]);

  function buildCommentTree(flat: Comment[]) {
    const map = new Map<number, Comment & { children: Comment[] }>();
    const roots: (Comment & { children: Comment[] })[] = [];
    flat.forEach((c) => {
      (c as any).children = [];
      map.set(c.id, c as any);
    });
    flat.forEach((c) => {
      if (c.parent_id) {
        const parent = map.get(c.parent_id);
        if (parent) parent.children.push(c as any);
      } else {
        roots.push(c as any);
      }
    });
    return roots;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!content.trim() || !user) return;
    setLoading(true);
    await supabase.from("comments").insert([
      {
        post_id: postId,
        parent_id: null,
        author_id: user.id,
        nickname:
          isAbtalk || isAnonymousBoard ? "" : user.nickname,
        writer_type: user.userType,
        content,
      },
    ]);
    setContent("");
    setLoading(false);
    fetchComments();
  }

  async function handleReplySubmit(parentId: number) {
    const rContent = replyContent[parentId]?.trim();
    if (!rContent || !user) return;
    setLoading(true);
    await supabase.from("comments").insert([
      {
        post_id: postId,
        parent_id: parentId,
        author_id: user.id,
        nickname:
          isAbtalk || isAnonymousBoard ? "" : user.nickname,
        writer_type: user.userType,
        content: rContent,
      },
    ]);
    setReplyContent((rc) => ({ ...rc, [parentId]: "" }));
    setReplyTo(null);
    setLoading(false);
    fetchComments();
  }

  async function handleDeleteComment(commentId: number) {
    if (!window.confirm("정말 삭제하시겠습니까?")) return;
    await supabase.from("comments").delete().eq("id", commentId);
    fetchComments();
  }

  function startEdit(comment: Comment) {
    setEditingId(comment.id);
    setEditingContent(comment.content);
  }

  async function saveEdit(commentId: number) {
    if (!editingContent.trim()) return;
    await supabase
      .from("comments")
      .update({ content: editingContent })
      .eq("id", commentId);
    setEditingId(null);
    setEditingContent("");
    fetchComments();
  }

  function cancelEdit() {
    setEditingId(null);
    setEditingContent("");
  }

  function canEditOrDelete(comment: Comment) {
    return !!user && (user.id === comment.author_id || user.userType === "admin");
  }

  function getDisplayName(c: Comment) {
    // ✅ AB-talk: 기존 익명 규칙 유지
    if (isAbtalk) {
      if (["artist", "worker", "fan"].includes(c.writer_type || "")) {
        const type = typeLabel[c.writer_type || ""] || "";
        return type ? `익명 ${type}` : "익명";
      }
      const boardTypeLabel = typeLabel[boardType || ""] || "";
      return boardTypeLabel ? `익명 ${boardTypeLabel}` : "익명";
    }

    // ✅ 익명게시판: 항상 "익명"
    if (isAnonymousBoard) {
      return "익명";
    }

    // ✅ 일반 게시판
    return c.nickname || "익명";
  }

  function renderComment(
    c: Comment & { children?: Comment[] },
    depth = 0
  ) {
    const myVote = myReactions?.[`comment|${c.id}`];
    return (
      <li
        key={c.id}
        className={`bg-gray-50 px-3 py-2 rounded-lg shadow-sm text-sm flex flex-col ${
          depth > 0 ? "ml-8 border-l-2 border-gray-200" : ""
        }`}
      >
        <div className="flex items-center justify-between">
          <span className="font-semibold">{getDisplayName(c)}</span>
          {canEditOrDelete(c) && (
            <div className="flex gap-2">
              {editingId !== c.id && (
                <button
                  className="text-xs text-blue-400 hover:underline"
                  onClick={() => startEdit(c)}
                >
                  수정
                </button>
              )}
              <button
                className="text-xs text-red-500 hover:underline"
                onClick={() => handleDeleteComment(c.id)}
              >
                삭제
              </button>
            </div>
          )}
        </div>
        {editingId !== c.id ? (
          <div className="whitespace-pre-wrap">{c.content}</div>
        ) : (
          <div className="flex flex-col gap-2 mt-1">
            <textarea
              className="flex-1 border border-gray-300 rounded-lg px-3 py-2 min-h-[72px] max-h-56 resize-y focus:outline-none focus:ring-2 focus:ring-blue-300"
              rows={3}
              value={editingContent}
              onChange={(e) => setEditingContent(e.target.value)}
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <button
                className="px-3 py-1 bg-blue-300 text-white text-sm rounded-md hover:bg-blue-400"
                onClick={() => saveEdit(c.id)}
                type="button"
              >
                저장
              </button>
              <button
                className="px-3 py-1 bg-gray-200 text-gray-700 text-sm rounded-md hover:bg-gray-300"
                onClick={cancelEdit}
                type="button"
              >
                취소
              </button>
            </div>
          </div>
        )}
        <div className="flex items-center gap-5 text-xs text-gray-500 mt-1">
          <span>{new Date(c.created_at).toLocaleString()}</span>
          <button
            className={`flex items-center gap-1 ${
              myVote === 1 ? "text-blue-400 font-bold" : "text-gray-400"
            }`}
            onClick={() => handleReaction("comment", c.id, 1)}
            disabled={!user}
          >
            <ThumbsUp size={15} strokeWidth={2} />
            <span>{countCommentLikes(c.id, 1)}</span>
          </button>
          {user && (
            <button
              className="ml-0 text-blue-400 hover:underline"
              onClick={() =>
                setReplyTo(replyTo === c.id ? null : c.id)
              }
            >
              대댓글
            </button>
          )}
        </div>
        {replyTo === c.id && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleReplySubmit(c.id);
            }}
            className="mt-2 flex flex-col gap-2"
          >
            <textarea
              className="flex-1 border border-gray-300 rounded-lg px-3 py-2 min-h-[72px] max-h-56 resize-y focus:outline-none focus:ring-2 focus:ring-blue-300"
              rows={3}
              value={replyContent[c.id] || ""}
              onChange={(e) =>
                setReplyContent((rc) => ({
                  ...rc,
                  [c.id]: e.target.value,
                }))
              }
              placeholder="답글을 입력하세요"
              disabled={loading}
              autoFocus
            />
            <div className="flex justify-end">
              <button
                type="submit"
                className="bg-blue-300 text-white px-4 py-1.5 text-sm rounded-md hover:bg-blue-400"
                disabled={loading || !(replyContent[c.id]?.trim())}
              >
                등록
              </button>
            </div>
          </form>
        )}
        {c.children && c.children.length > 0 && (
          <ul className="space-y-2 mt-2">
            {c.children.map((child) => renderComment(child, depth + 1))}
          </ul>
        )}
      </li>
    );
  }

  const commentTree = buildCommentTree(comments);

  return (
    <div className="mt-6">
      <h3 className="font-semibold mb-3 text-lg">댓글</h3>
      {user && user.level === "full" ? (
        <form onSubmit={handleSubmit} className="mb-4 flex flex-col gap-2">
          <textarea
            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 min-h-[96px] max-h-60 resize-y focus:outline-none focus:ring-2 focus:ring-blue-300"
            rows={4}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="댓글을 입력하세요"
            disabled={loading}
          />
          <div className="flex justify-end">
            <button
              type="submit"
              className="bg-blue-300 text-white px-4 py-1.5 text-sm rounded-md hover:bg-blue-400"
              disabled={loading || !content.trim()}
            >
              등록
            </button>
          </div>
        </form>
      ) : (
        <div className="text-gray-400 mb-2">
          정회원 등업 후 댓글 작성 가능
        </div>
      )}
      <ul className="space-y-3">
        {commentTree.map((c) => renderComment(c))}
        {comments.length === 0 && (
          <div className="text-gray-400">댓글이 없습니다.</div>
        )}
      </ul>
    </div>
  );
}
